import { useQuery } from '@tanstack/react-query'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { getJSON } from '@/lib/api'
import { categoryLabels } from './engagementListColumns'
import { changeEngagement, type EngagementListKey } from './engagementListState'

type Options = { value: string; label: string }[]
const options = (values: Record<string, string>): Options =>
  Object.entries(values).map(([value, label]) => ({ value, label }))
function Facet({
  label,
  name,
  values,
  params,
}: {
  label: string
  name: string
  values: Options
  params: URLSearchParams
}) {
  const value = params.get(name)
  return (
    <FilterDropdown
      label={label}
      emptyLabel={label}
      options={values}
      selected={new Set(value ? [value] : [])}
      onChange={(next) =>
        changeEngagement({ [name]: [...next].find((item) => item !== value) ?? '' })
      }
    />
  )
}
function LevelFacet({ params }: { params: URLSearchParams }) {
  const query = useQuery({
    queryKey: ['engagement-membership-levels'],
    queryFn: () =>
      getJSON<{
        membership_levels: { id: number; name: string; kind: string; archived: boolean }[]
      }>('/api/v1/membership_levels'),
  })
  const values = (query.data?.membership_levels ?? [])
    .filter((level) => level.kind === 'individual')
    .map((level) => ({
      value: String(level.id),
      label: level.name,
    }))
  return <Facet label="Level" name="filters[level]" values={values} params={params} />
}
const filterDefinitions = {
  courses_completed: [
    {
      label: 'Status',
      key: 'status',
      values: options({
        completed: 'Completed',
        enrolled: 'In progress',
        failed: 'Failed',
        withdrawn: 'Withdrawn',
      }),
    },
  ],
  email_clicks: [{ label: 'Clicked', key: 'clicked', values: options({ yes: 'Yes', no: 'No' }) }],
  personal_memberships: [],
  spend: [
    { label: 'Category', key: 'category', values: options(categoryLabels) },
    {
      label: 'Billed to',
      key: 'billed_to',
      values: options({ organization: 'Organization', person: 'Person' }),
    },
    { label: 'Unpaid', key: 'unpaid', values: options({ yes: 'Unpaid only' }) },
  ],
}
export function EngagementListFilters({
  tile,
  params,
}: {
  tile: EngagementListKey
  params: URLSearchParams
}) {
  return (
    <>
      {tile !== 'personal_memberships' && (
        <Facet
          label="People"
          name="person_status"
          values={options({ current: 'Current', former: 'Former' })}
          params={params}
        />
      )}
      {filterDefinitions[tile].map((filter) => (
        <Facet
          key={filter.key}
          label={filter.label}
          name={`filters[${filter.key}]`}
          values={filter.values}
          params={params}
        />
      ))}
      {tile === 'personal_memberships' && <LevelFacet params={params} />}
    </>
  )
}
